import { useEffect, lazy, Suspense } from 'react';
import './App.css';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Routes, Route, useLocation, Outlet } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ClientAuthProvider } from './context/ClientAuthContext';
import { ThemeProvider, LanguageProvider } from './context/UIContext';
import { Toaster } from './components/ui/toaster';
import CookieBanner from './components/CookieBanner';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';

// Lazy-loaded chunks — keeps the initial bundle small so the landing page
// paints fast. Non-landing pages, admin flows and heavy 3D widgets ship as
// separate chunks and arrive only when the user navigates to them.
const DigitalAdvisor = lazy(() => import('./components/DigitalAdvisor'));
const FloatingContactButton = lazy(() => import('./components/FloatingContactButton'));

const ChiSiamo = lazy(() => import('./pages/ChiSiamo'));
const ACDT = lazy(() => import('./pages/ACDT'));
const B100 = lazy(() => import('./pages/B100'));
const EnergyPower = lazy(() => import('./pages/EnergyPower'));
const Robotica = lazy(() => import('./pages/Robotica'));
const Servizi = lazy(() => import('./pages/Servizi'));
const ServiceDetail = lazy(() => import('./pages/ServiceDetail'));
const Formazione = lazy(() => import('./pages/Formazione'));
const CorsiCertificati = lazy(() => import('./pages/CorsiCertificati'));
const CorsoCertificatoDetail = lazy(() => import('./pages/CorsoCertificatoDetail'));
const ConsulenzaAIAct = lazy(() => import('./pages/ConsulenzaAIAct'));
const LandingDigCompEDU = lazy(() => import('./pages/LandingDigCompEDU'));
const LandingAlfabetizzazione = lazy(() => import('./pages/LandingAlfabetizzazione'));
const LandingConsulenteDigitale = lazy(() => import('./pages/LandingConsulenteDigitale'));
const Rinnovi = lazy(() => import('./pages/Rinnovi'));
const DigitalScore = lazy(() => import('./pages/DigitalScore'));
const Blog = lazy(() => import('./pages/Blog'));
const Contatti = lazy(() => import('./pages/Contatti'));
const ContractChargebull = lazy(() => import('./pages/ContractChargebull'));
const Legal = lazy(() => import('./pages/Legal'));
const ClubLanding = lazy(() => import('./pages/ClubLanding'));
const ClubDashboard = lazy(() => import('./pages/ClubDashboard'));
const VerifyCertificate = lazy(() => import('./pages/VerifyCertificate'));

const AcademyLayout = lazy(() => import('./components/academy/AcademyLayout'));
const Auth = lazy(() => import('./pages/academy/Auth'));
const Catalog = lazy(() => import('./pages/academy/Catalog'));
const Dashboard = lazy(() => import('./pages/academy/Dashboard'));
const MieiCorsi = lazy(() => import('./pages/academy/MieiCorsi'));
const Percorsi = lazy(() => import('./pages/academy/Percorsi'));
const CourseDetail = lazy(() => import('./pages/academy/CourseDetail'));
const CoursePlayer = lazy(() => import('./pages/academy/CoursePlayer'));
const Admin = lazy(() => import('./pages/academy/Admin'));
const LeadsPanel = lazy(() => import('./pages/academy/LeadsPanel'));
const EmailsPanel = lazy(() => import('./pages/academy/EmailsPanel'));
const ClubAdmin = lazy(() => import('./pages/academy/ClubAdmin'));
const Report = lazy(() => import('./pages/academy/Report'));
const Gestione = lazy(() => import('./pages/academy/Gestione'));
const Venditore = lazy(() => import('./pages/academy/Venditore'));
const RoboticaLeads = lazy(() => import('./pages/academy/RoboticaLeads'));
const VendorsManage = lazy(() => import('./pages/academy/VendorsManage'));
const SalesAutomation = lazy(() => import('./pages/academy/SalesAutomation'));
const AdvisorSettings = lazy(() => import('./pages/academy/AdvisorSettings'));
const SavingCasesAdmin = lazy(() => import('./pages/academy/SavingCasesAdmin'));
const GuadagnaConIlDigitale = lazy(() => import('./pages/GuadagnaConIlDigitale'));
const ComparaSemplicePage = lazy(() => import('./pages/ComparaSemplicePage'));
const PaymentResult = lazy(() => import('./pages/academy/PaymentResult'));
const Profilo = lazy(() => import('./pages/academy/Profilo'));
const ForgotPassword = lazy(() => import('./pages/academy/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/academy/ResetPassword'));
const ClientAuth = lazy(() => import('./pages/clienti/ClientAuth'));
const ClientDashboard = lazy(() => import('./pages/clienti/ClientDashboard'));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Minimal placeholder shown while a lazy chunk loads. Keeps layout stable
// without flashing a spinner on fast connections.
function RouteFallback() {
  return (
    <div
      aria-busy="true"
      className="min-h-[60vh] flex items-center justify-center bg-background"
      data-testid="route-fallback"
    >
      <div className="w-10 h-10 rounded-full border-2 border-lime/30 border-t-lime animate-spin" />
    </div>
  );
}

function MainLayout() {
  return (
    <>
      <Navbar />
      <Suspense fallback={<RouteFallback />}>
        <Outlet />
      </Suspense>
      <Footer />
      {/* Advisor + floating button load asynchronously so they do not block
          the hero or first paint. */}
      <Suspense fallback={null}>
        <DigitalAdvisor />
        <FloatingContactButton />
      </Suspense>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
      <ThemeProvider>
      <LanguageProvider>
      <AuthProvider>
        <ClientAuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route element={<MainLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/chi-siamo" element={<ChiSiamo />} />
              <Route path="/servizi" element={<Servizi />} />
              <Route path="/servizi/compara-semplice" element={<ComparaSemplicePage />} />
              <Route path="/servizi/:slug" element={<ServiceDetail />} />
              <Route path="/guadagna-con-il-digitale" element={<GuadagnaConIlDigitale />} />
              <Route path="/formazione" element={<Formazione />} />
              <Route path="/formazione/corsi-certificati" element={<CorsiCertificati />} />
              <Route path="/formazione/corsi-certificati/:slug" element={<CorsoCertificatoDetail />} />
              <Route path="/consulenza-ai-act" element={<ConsulenzaAIAct />} />
              <Route path="/formazione/digcompedu" element={<LandingDigCompEDU />} />
              <Route path="/formazione/alfabetizzazione-digitale" element={<LandingAlfabetizzazione />} />
              <Route path="/formazione/consulente-digitale-sostenibilita" element={<LandingConsulenteDigitale />} />
              <Route path="/rinnovi" element={<Rinnovi />} />
              <Route path="/digital-score" element={<DigitalScore />} />
              <Route path="/club" element={<ClubLanding />} />
              <Route path="/acdt" element={<ACDT />} />
              <Route path="/b100" element={<B100 />} />
              <Route path="/energy-power" element={<EnergyPower />} />
              <Route path="/robotica" element={<Robotica />} />
              <Route path="/energy-power/contratto" element={<ContractChargebull />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/contatti" element={<Contatti />} />
              <Route path="/privacy-policy" element={<Legal type="privacy" />} />
              <Route path="/cookie-policy" element={<Legal type="cookie" />} />
              <Route path="/termini" element={<Legal type="terms" />} />
            </Route>

            <Route path="/academy/login" element={<Auth />} />
            <Route path="/club/dashboard" element={<ClubDashboard />} />
            <Route path="/academy/password-dimenticata" element={<ForgotPassword />} />
            <Route path="/academy/reset-password" element={<ResetPassword />} />
            <Route path="/verifica/:certId" element={<VerifyCertificate />} />
            <Route path="/clienti/login" element={<ClientAuth />} />
            <Route path="/clienti" element={<ClientDashboard />} />
            <Route path="/payment/success" element={<PaymentResult />} />
            <Route path="/payment/cancel" element={<PaymentResult />} />
            <Route path="/academy" element={<AcademyLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="catalogo" element={<Catalog />} />
              <Route path="miei-corsi" element={<MieiCorsi />} />
              <Route path="percorsi" element={<Percorsi />} />
              <Route path="corso/:slug" element={<CourseDetail />} />
              <Route path="impara/:slug" element={<CoursePlayer />} />
              <Route path="admin" element={<Admin />} />
              <Route path="leads" element={<LeadsPanel />} />
              <Route path="emails" element={<EmailsPanel />} />
              <Route path="club" element={<ClubAdmin />} />
              <Route path="gestione" element={<Gestione />} />
              <Route path="venditore" element={<Venditore />} />
              <Route path="robotica-leads" element={<RoboticaLeads />} />
              <Route path="vendors-manage" element={<VendorsManage />} />
              <Route path="sales" element={<SalesAutomation />} />
              <Route path="advisor-settings" element={<AdvisorSettings />} />
              <Route path="saving-cases" element={<SavingCasesAdmin />} />
              <Route path="report" element={<Report />} />
              <Route path="profilo" element={<Profilo />} />
            </Route>
          </Routes>
          </Suspense>
          <Toaster />
          <CookieBanner />
        </BrowserRouter>
        </ClientAuthProvider>
      </AuthProvider>
      </LanguageProvider>
      </ThemeProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
